import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, X, Zap, Flame, Trophy, Loader2, PartyPopper } from "lucide-react";
import { TVWidget } from "@/components/TradingView";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Lesson() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/trainer/lessons/${id}`, { credentials: "include" });
        if (r.ok) setLesson(await r.json());
        else navigate("/learn");
      } catch { navigate("/learn"); }
    })();
  }, [id, navigate]);

  const submit = async () => {
    if (lesson.quiz.length && Object.keys(answers).length < lesson.quiz.length) {
      toast("Answer every question first"); return;
    }
    setSubmitting(true);
    try {
      const arr = lesson.quiz.map((_, i) => (answers[i] ?? -1));
      const r = await fetch(`${API}/trainer/lessons/${id}/complete`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ answers: arr }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      setResult(d);
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  if (!lesson) return <div className="min-h-screen bg-[#0A0A0A] grid place-items-center"><Loader2 className="h-6 w-6 text-zinc-600 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA]">
      <div className="grain" />
      <header className="sticky top-0 z-30 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/8">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/learn" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="lesson-back">
            <ArrowLeft className="h-4 w-4" /> Trainer
          </Link>
          <span className="section-label text-[9px] text-[#e6c67a]">+{lesson.xp} XP</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="lesson-page">
        <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight">{lesson.title}</h1>
        <p className="mt-3 text-base text-zinc-400 font-light">{lesson.summary}</p>

        {lesson.chart_symbol && (
          <div className="mt-6 rounded-xl overflow-hidden border border-white/8 h-[220px]">
            <TVWidget src="https://s3.tradingview.com/external-embedding/embed-widget-mini-symbol-overview.js" height={220}
              config={{ symbol: lesson.chart_symbol, width: "100%", height: "100%", locale: "en", dateRange: "3M",
                        colorTheme: "dark", isTransparent: true, autosize: true }} />
          </div>
        )}

        <div className="mt-8 text-[15px] text-zinc-200 font-light leading-relaxed whitespace-pre-wrap" data-testid="lesson-content">
          {lesson.content}
        </div>

        {!result && lesson.quiz.length > 0 && (
          <div className="mt-12 space-y-8" data-testid="lesson-quiz">
            <div className="section-label text-[10px] text-[#9fd3ff]">Quick check</div>
            {lesson.quiz.map((q, qi) => (
              <div key={qi} data-testid={`quiz-q-${qi}`}>
                <div className="text-sm text-zinc-100 font-medium">{qi + 1}. {q.q}</div>
                <div className="mt-3 space-y-2">
                  {q.options.map((opt, oi) => (
                    <button key={oi} onClick={() => setAnswers({ ...answers, [qi]: oi })}
                      className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-colors ${answers[qi] === oi ? "border-[#9fd3ff] bg-[#9fd3ff]/10 text-white" : "border-white/10 text-zinc-300 hover:border-white/25"}`}
                      data-testid={`quiz-q-${qi}-opt-${oi}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <button onClick={submit} disabled={submitting} className="btn-solid w-full py-3.5 text-sm font-medium disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="lesson-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit & earn XP"}
            </button>
          </div>
        )}

        {!result && lesson.quiz.length === 0 && (
          <button onClick={submit} disabled={submitting} className="mt-12 btn-solid w-full py-3.5 text-sm font-medium disabled:opacity-60" data-testid="lesson-submit">
            {submitting ? "…" : "Mark complete & earn XP"}
          </button>
        )}

        {result && (
          <div className={`mt-10 glass-card glass-card-featured p-8 text-center`} data-testid="lesson-result">
            {result.passed ? <PartyPopper className="h-10 w-10 text-[#7ee0a1] mx-auto" /> : <X className="h-10 w-10 text-[#ff7a7a] mx-auto" />}
            <h2 className="mt-4 font-heading text-2xl font-light">
              {result.passed ? "Lesson complete!" : "Almost there"}
            </h2>
            <p className="mt-2 text-sm text-zinc-400 font-light">You scored {result.score}%.
              {result.passed ? ` +${result.xp_awarded} XP earned.` : " You need 60% to pass — review and try again."}</p>
            {result.passed && (
              <div className="mt-5 flex items-center justify-center gap-4 text-sm">
                <span className="inline-flex items-center gap-1.5 text-[#e6c67a]"><Zap className="h-4 w-4" /> {result.xp_total} XP</span>
                <span className="inline-flex items-center gap-1.5 text-[#ff9d5c]"><Flame className="h-4 w-4" /> {result.streak} day streak</span>
                <span className="inline-flex items-center gap-1.5 text-[#9fd3ff]"><Trophy className="h-4 w-4" /> {result.stage}</span>
              </div>
            )}
            <div className="mt-7 flex items-center justify-center gap-3">
              {result.passed ? (
                <Link to="/learn" className="btn-solid px-6 py-3 text-sm font-medium">Continue</Link>
              ) : (
                <button onClick={() => { setResult(null); setAnswers({}); }} className="btn-solid px-6 py-3 text-sm font-medium" data-testid="lesson-retry">Try again</button>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
